// Imperative Leaflet layer for a meetup: markers are diffed by id, the focus circle glides.
import L from 'leaflet';
import type { MeetupSnapshot } from '../../shared/schemas.ts';
import type { LatLon } from '../../src/domain/geo.ts';
import { config, initials, modeEmoji } from '../lib.ts';

export interface MapHandlers {
  onPick(p: LatLon): void;
  onSelect(candidateId: string): void;
  onHover(candidateId: string | null): void;
}

const el = (cls: string, text: string, color?: string) => {
  const d = document.createElement('div');
  d.className = cls;
  d.textContent = text;
  if (color) d.style.background = color;
  return d;
};
const icon = (node: HTMLElement, size = 30) =>
  L.divIcon({ className: '', html: node, iconSize: [size, size], iconAnchor: [size / 2, size / 2] });
const tip = (text: string) => {
  const s = document.createElement('span');
  s.textContent = text;
  return s;
};
const ease = (t: number) => 1 - (1 - t) ** 3;

export class MeetupMap {
  map: L.Map;
  private members = new Map<string, { m: L.Marker; key: string }>();
  private cands = new Map<string, { m: L.Marker; key: string }>();
  private areas = L.layerGroup();
  private lines = L.layerGroup();
  private focus: L.Circle | null = null;
  private focusDot: L.Marker | null = null;
  private pin: L.Marker | null = null;
  private anim = 0;
  private fitted = '';
  private last: MeetupSnapshot | null = null;

  private h: MapHandlers;

  constructor(node: HTMLElement, handlers: MapHandlers) {
    this.h = handlers;
    this.map = L.map(node, { zoomControl: true, attributionControl: true }).setView([52.52, 13.405], 12);
    // Tiles come from our own origin (relayed + cached by the server).
    L.tileLayer('/tiles/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: config.value?.tileAttribution ?? '&copy; OpenStreetMap contributors',
    }).addTo(this.map);
    this.areas.addTo(this.map);
    this.lines.addTo(this.map);
    this.map.on('click', (e: L.LeafletMouseEvent) => this.h.onPick({ lat: e.latlng.lat, lon: e.latlng.lng }));
    // Right-click on desktop, long-press on touch.
    this.map.on('contextmenu', (e: L.LeafletMouseEvent) => this.h.onPick({ lat: e.latlng.lat, lon: e.latlng.lng }));
  }

  update(s: MeetupSnapshot, hover: string | null) {
    this.last = s;
    // members
    const seen = new Set<string>();
    for (const p of s.members) {
      if (p.lat == null || p.lon == null) continue;
      seen.add(p.id);
      const key = `${p.name}|${p.color}|${p.mode}`;
      const cur = this.members.get(p.id);
      if (cur && cur.key === key) {
        cur.m.setLatLng([p.lat, p.lon]);
        continue;
      }
      cur?.m.remove();
      const node = el(`pin member${p.isMe ? ' me' : ''}`, initials(p.name), p.color);
      // Everyone's travel mode as a small badge on their pin.
      const badge = document.createElement('span');
      badge.className = 'mode-badge';
      badge.textContent = modeEmoji(p.mode);
      node.append(badge);
      const m = L.marker([p.lat, p.lon], { icon: icon(node), zIndexOffset: p.isMe ? 600 : 400, keyboard: false })
        .bindTooltip(tip(`${p.name}${p.isMe ? ' (you)' : ''} ${modeEmoji(p.mode)}`), {
          direction: 'top',
          offset: [0, -14],
        })
        .addTo(this.map);
      this.members.set(p.id, { m, key });
    }
    for (const [id, x] of this.members) {
      if (seen.has(id)) continue;
      x.m.remove();
      this.members.delete(id);
    }

    // candidates
    const cseen = new Set<string>();
    for (const c of s.candidates) {
      cseen.add(c.id);
      const win = c.id === s.decidedId;
      const key = `${c.rank}|${win}|${c.source}|${c.name}|${c.id === hover}`;
      const cur = this.cands.get(c.id);
      if (cur && cur.key === key) continue;
      cur?.m.remove();
      const cls = `pin cand${win ? ' win' : ''}${c.rank === 1 && !s.decidedId ? ' lead' : ''}${c.source === 'auto' ? ' auto' : ''}${c.id === hover ? ' hl' : ''}`;
      const m = L.marker([c.lat, c.lon], {
        icon: icon(el(cls, win ? '★' : String(c.rank))),
        zIndexOffset: 1000 - c.rank,
        keyboard: false,
      })
        .bindTooltip(tip(c.name), { direction: 'top', offset: [0, -14] })
        .on('click', () => this.h.onSelect(c.id))
        .on('mouseover', () => this.h.onHover(c.id))
        .on('mouseout', () => this.h.onHover(null))
        .addTo(this.map);
      this.cands.set(c.id, { m, key });
    }
    for (const [id, x] of this.cands) {
      if (cseen.has(id)) continue;
      x.m.remove();
      this.cands.delete(id);
    }

    // runner-up areas (faint), then the focus (animated)
    this.areas.clearLayers();
    if (!s.decidedId) {
      for (const a of s.areas.slice(1, 4))
        L.circle([a.lat, a.lon], { radius: a.radius, className: 'area-ring', interactive: false }).addTo(this.areas);
    }
    this.moveFocus(s.decidedId ? null : s.focus);
    this.drawLines(hover);

    // fit once per structural change (who/what is on the map), not on every live ping
    const fitKey = `${s.members.filter((m) => m.lat != null).length}|${s.candidates.length}|${s.decidedId}`;
    if (fitKey !== this.fitted) {
      this.fitted = fitKey;
      const pts: L.LatLngTuple[] = [
        ...s.members.filter((m) => m.lat != null).map((m) => [m.lat!, m.lon!] as L.LatLngTuple),
        ...s.candidates.map((c) => [c.lat, c.lon] as L.LatLngTuple),
      ];
      if (pts.length > 1) this.map.fitBounds(pts, { padding: [36, 36], maxZoom: 15, animate: true });
      else if (pts.length === 1) this.map.setView(pts[0], 14);
    }
  }

  private moveFocus(f: (LatLon & { radius: number }) | null) {
    cancelAnimationFrame(this.anim);
    if (!f) {
      this.focus?.remove();
      this.focusDot?.remove();
      this.focus = null;
      this.focusDot = null;
      return;
    }
    if (!this.focus) {
      this.focus = L.circle([f.lat, f.lon], { radius: f.radius, className: 'focus-ring', interactive: false }).addTo(
        this.map,
      );
      this.focusDot = L.marker([f.lat, f.lon], {
        icon: icon(el('pin focus', ''), 16),
        interactive: false,
        keyboard: false,
      }).addTo(this.map);
      return;
    }
    const from = this.focus.getLatLng();
    const r0 = this.focus.getRadius();
    const t0 = performance.now();
    const step = (t: number) => {
      const k = ease(Math.min(1, (t - t0) / 700));
      const ll: L.LatLngTuple = [from.lat + (f.lat - from.lat) * k, from.lng + (f.lon - from.lng) * k];
      this.focus?.setLatLng(ll).setRadius(r0 + (f.radius - r0) * k);
      this.focusDot?.setLatLng(ll);
      if (k < 1) this.anim = requestAnimationFrame(step);
    };
    this.anim = requestAnimationFrame(step);
  }

  drawLines(hover: string | null) {
    this.lines.clearLayers();
    const s = this.last;
    if (!s) return;
    const target = s.candidates.find((c) => c.id === (hover || s.decidedId));
    if (!target) return;
    for (const p of s.members) {
      if (p.lat == null || p.lon == null) continue;
      L.polyline(
        [
          [p.lat, p.lon],
          [target.lat, target.lon],
        ],
        { color: p.color, weight: 3, opacity: 0.7, dashArray: '6 6', interactive: false },
      ).addTo(this.lines);
    }
  }

  setPin(p: LatLon | null, onMove?: (p: LatLon) => void) {
    this.pin?.remove();
    this.pin = null;
    if (!p) return;
    this.pin = L.marker([p.lat, p.lon], {
      draggable: true,
      icon: icon(el('pin drop', '＋')),
      zIndexOffset: 2000,
    }).addTo(this.map);
    if (onMove)
      this.pin.on('dragend', () => onMove({ lat: this.pin!.getLatLng().lat, lon: this.pin!.getLatLng().lng }));
    if (!this.map.getBounds().contains([p.lat, p.lon])) this.map.setView([p.lat, p.lon], 15);
  }

  flyTo(p: LatLon) {
    this.map.flyTo([p.lat, p.lon], Math.max(this.map.getZoom(), 15), { duration: 0.6 });
  }

  destroy() {
    cancelAnimationFrame(this.anim);
    this.map.remove();
  }
}
